import * as React from "react";
import { 
    List,
    Datagrid, Filter, TopToolbar, SortButton, ExportButton,
    TextField, NumberField, FunctionField,
    ReferenceInput, SelectInput,
    useListContext, useListSortContext
} from 'react-admin';
import { TableHead, TableRow, TableCell, useMediaQuery, makeStyles } from '@material-ui/core';
import Config from "../../providers/configProvider";
import LogoField from '../../components/LogoField';

const CompetitionSeasonFilter = props => (
    <Filter {...props}>
        <ReferenceInput label="Comp Season" source="competitionSeasonId" reference="competition-season" perPage={1000} alwaysOn allowEmpty={false}>
            <SelectInput optionText={record => `${record.name} ${record.seasonYear}`} />
        </ReferenceInput>
    </Filter>
);

const ListActions = ({ sortFields }) => (
    <TopToolbar>
        <SortButton fields={sortFields} />
        <ExportButton />
    </TopToolbar>
);

const useStyles = makeStyles({
    cellSmall: {
        padding: '8px'
    },
    cell: {
        textAlign: 'right'
    },
    cellLeft: {
        textAlign: 'left'
    },
    cellBorder: {
        borderLeft: '1px solid #000',
        textAlign: 'right'
    },
    cellBorderLeft: {
        borderLeft: '1px solid #000',
        textAlign: 'left'
    },
    cellBorderDouble: {
        borderLeft: '3px double #000',
        textAlign: 'right'
    },
    topHeader: {
        fontWeight: 'bold',
        textAlign: 'center'
    },
    topHeaderBorder: {
        fontWeight: 'bold',
        textAlign: 'center',
        borderLeft: '1px solid #000'
    },
    topHeaderBorderDouble: {
        fontWeight: 'bold',
        textAlign: 'center',
        borderLeft: '3px double #000'
    },
    green: {
        fontWeight: 'bold',
        color: 'green'
    },
    greenSmall: {
        color: 'green'
    },
    red: {
        fontWeight: 'bold',
        color: 'red'
    },
    redSmall: {
        color: 'red'
    }
});

/*** DRAW DIFFICULTY ***/
const getDrawDifficultySortFields = data => {
    return [ 
        { field: 'games', label: 'games' },
        { field: 'top4', label: 'top 4' }, 
        { field: 'inFinals', label: `top ${data.teamsInFinals}` }, 
        { field: 'notInFinals', label: `bottom ${data.teamsNotInFinals}` }, 
        { field: 'bottom4', label: 'bottom 4' }, 
        { field: 'oppositionLadderPosition.total', label: 'opposition ladder position total' }, 
        { field: 'oppositionLadderPosition.average', label: 'opposition ladder position average' }
    ];
};

const DrawDifficultyListRowStyle = (record, index) => ({
    backgroundColor: Config.MyTeamCodes.includes(record.teamCode) ? '#efe' : '#fff'
});

const GetDrawDifficultyTeamsInFinals = () => {
    const { data, ids, total, loaded } = useListContext();

    let teamsInFinals = 0;
    let totalTeams = 0;

    if (loaded) {
        teamsInFinals = data[ids[0]].teamsInFinals;
        totalTeams = total;
    }

    return { teamsInFinals: teamsInFinals, teamsNotInFinals: totalTeams - teamsInFinals };
}

const DrawDifficultyHeader = ({ children }) => {
    const classes = useStyles();
    const { currentSort } = useListSortContext();
    const data = GetDrawDifficultyTeamsInFinals();
    const drawDifficultySortFields = getDrawDifficultySortFields(data);
    return (
        <TableHead>
            <TableRow>
                <TableCell colSpan={2} />
                <TableCell colSpan={2} className={classes.topHeader}>Games vs Top Opposition</TableCell>
                <TableCell colSpan={2} className={classes.topHeaderBorder}>Games vs Bottom Opposition</TableCell>
                <TableCell colSpan={2} className={classes.topHeaderBorder}>Opposition Ladder Position</TableCell>
            </TableRow>
            <TableRow>
                {React.Children.map(children, child => (
                    <TableCell key={child.props.source} className={child.props.source && ['top4','notInFinals','oppositionLadderPosition.total'].includes(child.props.source) ? classes.cellBorder : (child.props.label === 'Team' ? classes.cellLeft : classes.cell)}>
                        {child.props.label}{drawDifficultySortFields.find(f => f.field === child.props.source) && currentSort.field === drawDifficultySortFields.find(f => f.field === child.props.source).label
                            ? (currentSort.order === 'DESC' ? <span> &darr;</span> : <span> &uarr;</span>)
                            :  null}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
};

export const DrawDifficultyList = props => {
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const classes = useStyles();

    const DrawDifficultyListActions = () => {
        const data = GetDrawDifficultyTeamsInFinals();
        const drawDifficultySortFields = getDrawDifficultySortFields(data);
        return <ListActions sortFields={drawDifficultySortFields.map(f => f.label)} />;
    };

    const DrawDifficultyListContents = () => {

        const data = GetDrawDifficultyTeamsInFinals();
        const inFinalsLabel = `Top ${data.teamsInFinals}`;
        const notInFinalsLabel = `Bottom ${data.teamsNotInFinals}`;

        return isSmall ? (
            <Datagrid classes={{ rowCell: classes.cellSmall, headerCell: classes.cellSmall }} rowStyle={DrawDifficultyListRowStyle}>
                <LogoField type="teams" source="teamCode" text="teamCode" label="Team" />
                <TextField source="top4" label="Top 4" />
                <TextField source="inFinals" label={inFinalsLabel} />
                <TextField source="notInFinals" label={notInFinalsLabel} />
                <TextField source="bottom4" label="Bottom 4" />
                <TextField source="oppositionLadderPosition.average" label="Avg" />
            </Datagrid>
        ) : (
            <Datagrid header={<DrawDifficultyHeader />} classes={{ rowCell: classes.cell, headerCell: classes.cell }} rowStyle={DrawDifficultyListRowStyle}>
                <FunctionField render={record => record.competitionCode === 'NRL' 
                    ? <LogoField type="teams" source="teamCode" text="teamNickname" /> 
                    : <LogoField type="teams" source="teamCode" text="teamName" />
                } label="Team" cellClassName={classes.cellLeft} />
                <TextField source="games" label="Games" />
                <TextField source="top4" label="Top 4" cellClassName={classes.cellBorder} />
                <TextField source="inFinals" label={inFinalsLabel} />
                <TextField source="notInFinals" label={notInFinalsLabel} cellClassName={classes.cellBorder} />
                <TextField source="bottom4" label="Bottom 4" />
                <TextField source="oppositionLadderPosition.total" label="Total" cellClassName={classes.cellBorder} />
                <NumberField source="oppositionLadderPosition.average" label="Average" options={{ minimumFractionDigits: 2 }} />
            </Datagrid>
        )
    };

    return (
        <List {...props} 
            bulkActionButtons={false} 
            filters={<CompetitionSeasonFilter />}
            filterDefaultValues={{ competitionSeasonId: Config.CurrentCompetitionSeasonId }}
            actions={<DrawDifficultyListActions />}
            sort={{ field: 'opposition ladder position average', order: 'ASC' }}
            pagination={null}
        >
            <DrawDifficultyListContents />
        </List>
    );
};
